import { BaseItem, BaseContent } from 'components/Widget/Menu/style';
import styled from 'styled-components';
import { unthemed, layout, zIndex } from 'tokens';
const stripWidthCollapsed = 28;
const stripWidthDocked = 16;

interface BaseProps {
	$leftDocked: boolean;
	$rightDocked: boolean;
}
export const Base = styled.div<BaseProps>`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	height: 100%;
	display: grid;
	grid-template-rows: ${layout.inspectorNavbarHeight}px 1fr ${layout.frameBarHeight}px;
	grid-template-columns: ${({ $leftDocked, $rightDocked }) => {
		const left = $leftDocked ? `auto ${stripWidthDocked}px` : `${stripWidthCollapsed}px`;
		const right = $rightDocked ? `${stripWidthDocked}px auto` : `${stripWidthCollapsed}px`;
		return `${left} 1fr ${right}`;
	}};
	grid-template-areas: ${({ $leftDocked, $rightDocked }) => {
		const left = $leftDocked ? 'left-panel left-strip' : 'left-strip';
		const right = $rightDocked ? 'right-strip right-panel' : 'right-strip';
		const navCols = ($leftDocked ? 2 : 1) + 1 + ($rightDocked ? 2 : 1);
		const navRow = Array(navCols).fill('navbar').join(' ');
		return `"${navRow}"
			   "${left} prototype ${right}"
			   "${left} footer ${right}"`;
	}};
`;

export const PanelStrip = styled.div<{ $side: 'left' | 'right' }>`
	grid-area: ${({ $side }) => ($side === 'left' ? 'left-strip' : 'right-strip')};
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${({ theme }) => theme.gray[0]};
	${({ $side, theme }) =>
		$side === 'left'
			? `border-right: 1px solid ${theme.gray[200]};`
			: `border-left: 1px solid ${theme.gray[200]};`}
	color: ${({ theme }) => theme.gray[400]};
	transition: color 0.2s ease, background-color 0.2s ease;

	&:hover {
		color: ${({ theme }) => theme.gray[700]};
		background: ${({ theme }) => theme.gray[100]};
	}
`;

// Navbar wrapper for FrameInspector grid layout
export const NavbarWrapper = styled.div`
	grid-area: navbar;
`;

export const ToolButtonSpacer = styled.div`
	width: 8px;
`;

interface SelectButtonProps {
	active?: boolean;
	disabled?: boolean;
}

export const SelectButton = styled.button<SelectButtonProps>`
	background: ${({ theme, active }) => (active ? theme.gray[200] : 'transparent')};
	border: 1px solid ${({ theme, active }) => (active ? theme.gray[400] : theme.gray[400])};
	border-radius: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${({ theme }) => theme.gray[1000]};
	gap: 4px;
	padding-top: 7px;
	padding-bottom: 7px;
	padding-left: 8px;
	padding-right: 12px;
	outline: none; // to prevent outline appearing when we press Esc and this button is focused

	${({ disabled }) =>
		disabled &&
		`
		opacity: 0.5;
		pointer-events: none;
		cursor: not-allowed;
	`}
`;

export const Share = styled.div`
	margin-left: 8px;
	margin-right: 4px;
`;

// nested buttons are not allowed so we have a separate style for the dropdown trigger
export const DropdownTrigger = styled.div`
	background: transparent;
	border: none;
	border-radius: 4px;
	padding-left: 1px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 40px;

	&:hover {
		background: ${({ theme }) => theme.gray[100]};
	}
`;

export const StatusIndicator = styled.div`
	display: flex;
	justify-content: center;
	align-items: end;
	margin-bottom: 22px;
`;

export const OverlayText = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: calc(100% - 60px);
	max-width: 600px;
	padding-left: 30px;
	padding-right: 30px;
	margin-bottom: 20px;
	font-family: 'JetBrains Mono', sans-serif;
	font-size: 18px;
	font-weight: 600;
	color: ${unthemed.gray[100]};
	text-align: center;
	line-height: 1.7;
	word-break: break-word;
	overflow-wrap: break-word;
`;

export const ContextMenuWrapper = styled.div<{ x: number; y: number; isVisible?: boolean }>`
	position: fixed;
	top: ${(props) => props.y}px;
	left: ${(props) => props.x}px;
	z-index: ${zIndex.contextMenu};
	background: none;
	border: none;
	opacity: ${(props) => (props.isVisible ? 1 : 0)};
	pointer-events: ${(props) => (props.isVisible ? 'auto' : 'none')};
	transition: opacity 0.2s ease-in-out;
	transform: translateY(-100%);
`;

export const ContextMenu = styled.div`
	${BaseContent}
`;

export const Items = styled.div`
	${BaseItem}
	&:hover {
		background: ${({ theme }) => theme.blue[500]};
		outline: none;
	}
`;

export const PrototypeArea = styled.div`
	grid-area: prototype;
	position: relative;
	overflow: hidden;
`;

export const NavTitleClickable = styled.span`
	&:hover {
		opacity: 0.7;
	}
`;

export const NavTitleInput = styled.input`
	flex: 1;
	min-width: 0;
	height: var(--navbar-height);
	font-size: 14px;
	font-family: 'Inter', sans-serif;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[1000]};
	background: transparent;
	border: none;
	outline: none;
	margin-left: 6px;
	padding: 0;
`;

